import { type FC, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import { NavItemBadge } from './NavItemBadge.tsx';
import { useNavContext } from '../../NavContext.tsx';
import { userServerVars } from '../../userServerVars.ts';
import { hasKey } from 'dibs-ts-utils/exports/hasKey.ts';
import { isMobile } from 'dibs-media-query';
import { Tooltip } from 'dibs-elements/exports/Tooltip.tsx';
import Home from 'dibs-icons/exports/Home.ts';
import LightBulb from 'dibs-icons/exports/LightBulb.ts';
import Listings from 'dibs-icons/exports/Listings.ts';
import ShoppingBag from 'dibs-icons/exports/ShoppingBag.ts';
import Message from 'dibs-icons/exports/Message.ts';
import Analytics from 'dibs-icons/exports/Analytics.ts';
import Campaign from 'dibs-icons/exports/Campaign.ts';
import Finances from 'dibs-icons/exports/Finances.ts';
import Production from 'dibs-icons/exports/Production.ts';
import PersonSearch from 'dibs-icons/exports/PersonSearch.ts';
import BulletedList from 'dibs-icons/exports/BulletedList.ts';
import Dashboard from 'dibs-icons/exports/Dashboard.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './styles/NavItem.scss';

import { type NavItem_seller$key } from './__generated__/NavItem_seller.graphql.ts';
import { type NavItem_navItem$key } from './__generated__/NavItem_navItem.graphql.ts';
import { type NavItem_notifications$key } from './__generated__/NavItem_notifications.graphql.ts';
import { type NavItem_viewer$key } from './__generated__/NavItem_viewer.graphql.ts';

type Props = {
    seller?: NavItem_seller$key | null;
    navItem: NavItem_navItem$key;
    notifications?: NavItem_notifications$key | null;
    isActive?: boolean;
    isExpandingAreaItem?: boolean;
    viewer?: NavItem_viewer$key | null;
};

const sellerFragment = graphql`
    fragment NavItem_seller on Seller {
        ...NavItemBadge_seller
    }
`;

const navItemFragment = graphql`
    fragment NavItem_navItem on NavBarElement {
        type
        dataTn
        title
        eventName
    }
`;

const notificationsFragment = graphql`
    fragment NavItem_notifications on DealerNavBarBadges {
        ...NavItemBadge_notifications
    }
`;

const viewerFragment = graphql`
    fragment NavItem_viewer on Viewer {
        ...NavItemBadge_viewer
    }
`;

const NAV_ITEM_ICONS = {
    homeIcon: Home,
    recommendationsIcon: LightBulb,
    listingsIcon: Listings,
    ordersIcon: ShoppingBag,
    messageIcon: Message,
    bulletedListIcon: BulletedList,
    dashboardIcon: Dashboard,
    accountsIcon: PersonSearch,
    analyticsIcon: Analytics,
    advertisingIcon: Campaign,
    financeIcon: Finances,
    settingsIcon: Production,
} as const;

export const NavItem: FC<Props> = ({
    seller: sellerRef,
    navItem: navItemRef,
    notifications: notificationsRef,
    isExpandingAreaItem,
    isActive,
    viewer: viewerRef,
}) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const navItem = useFragment(navItemFragment, navItemRef);
    const notifications = useFragment(notificationsFragment, notificationsRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const { isDealer } = userServerVars;
    const { activeExpandedArea, expandedArea, status } = useNavContext();
    const isMobileView = isMobile();

    const { type, dataTn, title, eventName } = navItem || {};

    if (!title) {
        return null;
    }

    const isActiveExpandedArea = activeExpandedArea === eventName;
    const isExpandedArea = expandedArea === eventName;
    const shouldBeNoir =
        isActive || (isActiveExpandedArea && (status === 'collapsed' || !isExpandedArea));

    let textColor = dibsCss.textSatan;
    let fillColor = dibsCss.fillSatan;

    if (!isDealer) {
        textColor = dibsCss.textWhite;
        fillColor = dibsCss.fillWhite;
    } else if (shouldBeNoir) {
        textColor = dibsCss.textNoir;
        fillColor = dibsCss.fillNoir;
    }

    const isHighlightedItem = isActive || (status === 'collapsed' && isActiveExpandedArea);

    const Icon = hasKey(NAV_ITEM_ICONS, type) ? NAV_ITEM_ICONS[type] : null;

    return (
        <div
            className={classnames(styles.item, dibsCss.flex, dibsCss.pXsmall, dibsCss.relative, {
                [styles.desktopDealerItem]: !isMobileView && isDealer,
                [styles.desktopInternalItem]: !isMobileView && !isDealer,
                [styles.forceBaseHeight]: status === 'collapsed',
                [dibsCss.bgMoonstone]: isHighlightedItem && isDealer,
                [dibsCss.bgSatan]: isHighlightedItem && !isDealer,
                [dibsCss.mxXsmall]: !isMobileView && !isExpandingAreaItem,
                [dibsCss.mxSmall]: isMobileView && !isExpandingAreaItem,
            })}
            onMouseEnter={() => setTooltipOpen(true)}
            onMouseLeave={() => setTooltipOpen(false)}
        >
            <div
                className={classnames(styles.icon, fillColor, {
                    [dibsCss.mrSmaller]: status === 'expanded' || status === 'overlay',
                })}
            >
                {Icon ? <Icon variation="20" /> : <div className={dibsCss.w20px} />}
            </div>
            <div
                className={classnames(
                    styles.title,
                    dibsCss.flex,
                    dibsCss.sassyFontBodyMediumHeavy,
                    dibsCss.overflowHidden,
                    dibsCss.textEllipsis,
                    textColor,
                    {
                        [dibsCss.opacity100]: status === 'expanded' || status === 'overlay',
                        [dibsCss.opacity0]: status === 'collapsed',
                        [dibsCss.wFull]: status === 'expanded' || status === 'overlay',
                        [dibsCss.w0]: status === 'collapsed',
                    }
                )}
            >
                {title}
                {(status === 'expanded' || status === 'overlay') && (
                    <NavItemBadge
                        type={type}
                        dataTnKey={dataTn || ''}
                        notifications={notifications}
                        seller={seller}
                        viewer={viewer}
                    />
                )}
            </div>
            {status === 'collapsed' && (
                <NavItemBadge
                    type={type}
                    dataTnKey={dataTn || ''}
                    notifications={notifications}
                    seller={seller}
                    viewer={viewer}
                />
            )}
            <Tooltip
                isVisible={tooltipOpen && status === 'collapsed'}
                direction="right"
                paddingSize="none"
                type="autoWidth"
                onClose={() => setTooltipOpen(false)}
            >
                <div
                    className={classnames(
                        dibsCss.pxSmall,
                        dibsCss.pyXsmall,
                        dibsCss.sassyFontBodyMediumHeavy,
                        dibsCss.textSatan,
                        dibsCss.textCenter
                    )}
                >
                    {title}
                </div>
            </Tooltip>
        </div>
    );
};
